import clsx from 'clsx';
import React from 'react';
import { useRecoilValue } from 'recoil';
import { createStyles, makeStyles, Theme } from '@material-ui/core/styles';
import type { StandardDivProps } from '@sprinx/react-mui-components/StandardDiv';
import withThemeProps from '@sprinx/react-mui-components/withThemeProps';
import type { CatalogueProduct } from '../../../api/products/catalogue';
import { localeState } from '../../../api/appState';
import { getProductDescription } from '../../../helpers/getProductDescription';

export interface CataloguePageListContentItemCardDescriptionProps
  extends StandardDivProps<CataloguePageListContentItemCardDescriptionClassKey> {
  product: CatalogueProduct;
}

export type CataloguePageListContentItemCardDescriptionClassKey = 'root' | 'description';

const themeSettings = { name: 'CataloguePageListContentItemCardDescription' };
const useStyles = makeStyles(
  (theme: Theme) =>
    createStyles<CataloguePageListContentItemCardDescriptionClassKey, {}>({
      root: {
        ...theme.typography.body2,
        width: '100%',
        textAlign: 'left',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        maxHeight: '50px',
        display: '-webkit-box !important',
        '-webkit-line-clamp': 2,
        '-webkit-box-orient': 'vertical',
        whiteSpace: 'normal',
        fontSize: '80%',
        [theme.breakpoints.down('sm')]: {
          display: 'none !important',
        },
      },
      // removing default margin
      description: {
        '& > body': {
          margin: 0,
        },
        '& > p': {
          margin: 0,
        },
        '& > h1': {
          margin: 0,
        },
        '& > h2': {
          margin: 0,
        },
        '& > h3': {
          margin: 0,
        },
        '& > h4': {
          margin: 0,
        },
        '& > h5': {
          margin: 0,
        },
        '& > h6': {
          margin: 0,
        },
        '& > ul': {
          margin: 0,
          padding: 0,
        },
        '& > ol': {
          margin: 0,
          padding: 0,
        },
        '& > dl': {
          margin: 0,
        },
        '& > dt': {
          margin: 0,
        },
        '& > dd': {
          margin: 0,
        },
        '& > blockquote': {
          margin: 0,
        },
        '& > fieldset': {
          margin: 0,
          padding: 0,
        },
      },
    }),
  themeSettings,
);

const CataloguePageListContentItemCardDescription = React.forwardRef<
  HTMLDivElement,
  CataloguePageListContentItemCardDescriptionProps
>(({ className, classes: pClasses, product, ...rest }, ref) => {
  const classes = useStyles({ classes: pClasses });
  const locale = useRecoilValue(localeState);
  const description = getProductDescription(product, locale);

  return (
    <div ref={ref} className={clsx(classes.root, className)} {...rest}>
      {description ? <div className={classes.description}>{description}</div> : null}
    </div>
  );
});

CataloguePageListContentItemCardDescription.displayName = 'CataloguePageListContentItemCardDescription';

export default withThemeProps(themeSettings)(CataloguePageListContentItemCardDescription);
